import * as React from 'react'
import { ThemeProvider, useTheme } from 'styled-components'
import { useDebouncer } from '../../../../hooks/use-debouncer'
import type { SuppliersCustomerForCastingProps as Customer } from '../../../../services/json-schemas/serializers/suppliers-customer-for-casting-props'
import { customerIndex, type RansackQuery } from '../../../../services/models/customers.service'
import { I18nService } from '../../../../services/utils/i18n.service'
import { onlyUniqueByTransform } from '../../../../services/utils/lodash.service'
import HomonymTagComponent from '../../../shared/homonym-tag.component'
import InputTextComponent from '../../../shared/input-text.component'
import LoadingComponent from '../../../shared/loading.component'
import SelectableListComponent from '../../../shared/selectable-list.component'
import { NoResult } from '../../../plugins/casting/casting-styled.component'
import i18nScopedData from './customer-picker-modal.component.yml'

interface Props {
  query?: RansackQuery
  customer?: Customer
  maxListHeight?: string
  onResult: (_: Customer) => void
}

const i18nScoped = new I18nService(i18nScopedData)

export default function CustomerPickerModalComponent ({ query, customer, maxListHeight = '21em', onResult }: Props) {
  const theme = useTheme()

  const [ransackQuery, setRansackQuery] = React.useState<RansackQuery>({ ...query, 'q[sortable_name_or_reversed_sortable_name_cont]': '', page: 1, per_page: 15, scope: 'active', with_operator_segments_restriction: ['Customer'] })
  const [customers, setCustomers] = React.useState<Customer[]>([])
  const [loading, setLoading] = React.useState(true)
  const [hasMore, setHasMore] = React.useState(false)

  const execQuery = React.useCallback(async (fetchMore = false) => {
    setLoading(true)
    
    setRansackQuery(ransackQuery => {
      const nextQuery = { ...ransackQuery, page: fetchMore ? ransackQuery.page + 1 : 1 }

      void customerIndex(nextQuery, 'index_casting_form').then(fetched => {
        setCustomers(customers => fetchMore ? customers.concat(fetched).filter(onlyUniqueByTransform(_ => _.id)) : fetched)
        setHasMore(fetched.length === nextQuery.per_page)
        setLoading(false)
      })

      return nextQuery
    })
  }, [])

  const debouncer = useDebouncer(1000)
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { void execQuery() }, [])

  return <>
    <InputTextComponent
      id="js-test-find-customer-by-name"
      placeholder={ i18nScoped.t('find_by_name') }
      trimOnBlur={ false }
      value={ ransackQuery['q[sortable_name_or_reversed_sortable_name_cont]'] }
      onChange={ val => {
        setRansackQuery({
          ...ransackQuery,
          'q[sortable_name_or_reversed_sortable_name_cont]': val,
        })
        debouncer(() => execQuery())
      } } />

    <div style={{ maxHeight: maxListHeight, overflowY: 'auto' }}>
      <ThemeProvider theme={{ ...theme, selectableListOnComputerWithOuterBorder: false }}>
        <SelectableListComponent
          name="customer"
          data={ customers }
          value={ customer }
          formatter={ c => <>
            <div>
              { c.full_name }&nbsp;
              { c.homonym && <HomonymTagComponent className='u-mls' /> }&nbsp;
              { c.marketplace && window.currentUser.market_type === 'Supplier' && <span className="label">{ c.marketplace.name }</span> }
            </div>

            { c.homonym && <div className="u-txt--S">{ c.email }</div> }
          </> }
          onChange={ onResult } />
      </ThemeProvider>

      { loading && <LoadingComponent align='center' presentation='inline' /> }
      { !loading && customers.length === 0 && <NoResult>{ i18nScoped.t('no_customer_found') }</NoResult> }

      { hasMore && <p className="u-txtCenter">
        <a onClick={ () => void execQuery(true) }>{ i18nScoped.t('see_more') }</a>
      </p> }
    </div>
  </>
}
